import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Tabs } from '@libs/components';

import {
  CashTransactionActionEnum,
  FeatureModuleEnum,
  JurisdictionsEnum,
} from '@libs/backend-common';
import { WalletInfoItem } from '@libs/types';

import {
  useDepositWithdrawCurrencySelection,
  useTransactionStepper,
  useTranslation,
  useWalletAssets,
} from 'src/hooks';
import {
  TransactionLayout,
  DepositCrypto,
  FeatureRenderer,
  JurisdictionRenderer,
  PageContainer,
} from 'src/components';

import { CustomTabsCont } from './styles';

const DepositPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { nextStep, currentStep, prevStep, resetStepper } =
    useTransactionStepper();

  const { assets } = useWalletAssets();

  const walletInfo = assets.allAssets || [];

  const {
    activeTab,
    setActiveTab,
    defaultAsset,
    setDefaultAsset,
    isCryptoTabActive,
    tabItems: TRANSACTIONS_TAB_ITEMS,
  } = useDepositWithdrawCurrencySelection({
    resetStepper,
    assets: walletInfo,
    type: CashTransactionActionEnum.DEPOSIT,
  });

  const onBackButtonClick = () => {
    prevStep();
    setDefaultAsset(undefined);
    navigate('/deposit');
  };

  useEffect(() => {
    if (location.state?.shouldResetStep) {
      resetStepper();
    }
  }, [location.state, resetStepper]);

  const sortByDescendingOrder = (a: WalletInfoItem, b: WalletInfoItem) => {
    if (a.value > 0 || b.value > 0) {
      return b.value - a.value;
    } else {
      return (b?.orderingPriority ?? 0) - (a?.orderingPriority ?? 0);
    }
  };
  const cryptoAssets = assets.cryptoAssets.sort(sortByDescendingOrder);

  return (
    <FeatureRenderer
      acceptedFeature={[
        FeatureModuleEnum.CARD_DEPOSIT,
        FeatureModuleEnum.CASH_DEPOSIT,
        FeatureModuleEnum.BANK_WIRE_DEPOSIT,
        FeatureModuleEnum.CRYPTO_DEPOSIT,
      ]}
      shouldNavigate={true}
      shouldCheckKyc={false}
    >
      <PageContainer>
        <TransactionLayout
          onGoBack={onBackButtonClick}
          headTitle={t('dashboard_deposit_fill')}
          isBackButtonVisible={currentStep > 0}
        >
          {activeTab && currentStep === 0 && (
            <JurisdictionRenderer
              acceptedJurisdictions={[
                JurisdictionsEnum.GE,
                JurisdictionsEnum.EU,
              ]}
            >
              <CustomTabsCont>
                <Tabs
                  activeTab={activeTab}
                  tabs={TRANSACTIONS_TAB_ITEMS}
                  handleTabClick={setActiveTab}
                />
              </CustomTabsCont>
            </JurisdictionRenderer>
          )}
          {isCryptoTabActive && (
            <DepositCrypto
              defaultAsset={defaultAsset}
              currentStep={currentStep}
              assets={cryptoAssets}
              nextStep={nextStep}
              setActiveAsset={setDefaultAsset}
            />
          )}
        </TransactionLayout>
      </PageContainer>
    </FeatureRenderer>
  );
};

export default DepositPage;
